'use client'

import { memo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { Camera, Heart, LoaderCircle, Medal, Plus, Star } from 'lucide-react'
import { useCart, useWishlist, useProductCard, useImageCarousel } from '@/lib/commerce/hooks'
import { formatImageURL, formatPrice, getEffectivePrice, getStrikethroughPrice, getSalePercent, getDisplayDiscounts, isDiscountBetter, isCustomerAuthActive, getLoyaltyProductDiscount } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import VariantSelectionModal from '@/components/blocks/VariantSelectionModal'
import NoImage from '@/components/blocks/NoImage'
import ColorSwatches from '@/components/blocks/ColorSwatches'

function ProductCard({ product, vendor, showVariantName = false }: { product: any, vendor: any, showVariantName?: boolean }) {
  const { data: session } = useSession()
  const pathname = usePathname() || ''
  const { isInWishlist, toggle: toggleWishlist, loading: wishlistLoading } = useWishlist()
  const { isProductAvailable, availableVariant, handleAddToCart, showVariantModal, setShowVariantModal, handleVariantAddToCart, colors, activeColor, selectColor, cardProduct } = useProductCard(product, vendor, showVariantName)
  const [adding, setAdding] = useState(false)
  const { hoveredImageIndex, setHoveredImageIndex, allImages, remainingCount, shouldLoadImage, hasImages, onMouseEnter, onMouseLeave, handleTouchStart, handleTouchEnd } = useImageCarousel(cardProduct)

  const { loyaltySummary, loyaltyCategoryParents } = useCart()
  const discounts = getDisplayDiscounts(product, !!session?.user?.id, availableVariant?.id)
  const effectivePrice = getEffectivePrice(availableVariant, discounts, !!session?.user?.id)
  const strikethroughPrice = getStrikethroughPrice(availableVariant, discounts, !!session?.user?.id)
  const showsDiscountBadge = discounts?.length > 0 && isDiscountBetter(availableVariant, discounts, !!session?.user?.id)
  const loyaltyDeal = getLoyaltyProductDiscount({ product, variant: availableVariant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: showsDiscountBadge })
  const displayEffective = loyaltyDeal ? Math.max(0, (effectivePrice ?? availableVariant?.price ?? 0) - loyaltyDeal.unitSaving) : effectivePrice
  const displayStrike = loyaltyDeal ? (availableVariant?.price ?? 0) : strikethroughPrice
  const isDiscounted = displayEffective !== null
  const variantSalePct = getSalePercent(availableVariant)
  const badgePct = showsDiscountBadge
    ? (discounts.find((d: any) => d.type === 'productDiscount' && d.valueType === 'percent')?.valuePercent)
    : (variantSalePct > 0 && !loyaltyDeal?.saleOffset ? variantSalePct : null)

  const isWished = isInWishlist(product.id, product.variant?.id)
  const showWishlist = isCustomerAuthActive(vendor)
  const canPlaceOrder = vendor?.canPlaceOrder !== false
  const heroIndex = hoveredImageIndex ?? 0

  const onWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    toggleWishlist({ productId: product.id, variantId: product.variant?.id })
  }

  const onAddClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setAdding(true)
    try { await handleAddToCart() }
    finally { setAdding(false) }
  }

  return (
    <>
      <a
        href={navigateUrl(`/product/${product.customId || product.id}`, pathname)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className="group relative flex flex-col text-foreground"
      >
        <div className="relative isolate aspect-square rounded-2xl bg-foreground/5 overflow-hidden">
          {!hasImages && <NoImage />}
          {hasImages && allImages.map((img: string, i: number) => shouldLoadImage(i) && (
            <img
              key={i}
              src={formatImageURL(img)}
              alt={product.name}
              loading="lazy"
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${heroIndex === i ? 'opacity-100' : 'opacity-0'}`}
            />
          ))}
          {remainingCount > 0 && heroIndex === allImages.length - 1 && (
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-1 z-10 pointer-events-none">
              <Camera className="size-7 text-white" />
              <span className="text-white font-semibold text-[15px]">+{remainingCount} зураг</span>
            </div>
          )}
          {!isProductAvailable && (
            <div className="absolute inset-0 bg-background/70 inline-flex items-center justify-center text-foreground/70 text-[13px] font-semibold z-10">
            Дууссан
            </div>
          )}
          {(badgePct || loyaltyDeal) ? (
            <div className="absolute top-3 left-3 z-20 flex flex-wrap gap-1">
              {badgePct ? (
                <span className="inline-flex items-center px-2.5 h-6 rounded-full bg-[#FFC2DD] text-foreground text-[13px] font-semibold">
                -{badgePct}%
                </span>
              ) : null}
              {loyaltyDeal && (
                <span className="inline-flex items-center gap-1 px-2.5 h-6 rounded-full bg-violet-600 text-white text-[13px] font-semibold tabular-nums">
                  <Medal className="size-3.5" />
                  -{loyaltyDeal.percent}%
                </span>
              )}
            </div>
          ) : null}
          {showWishlist && (
            <button
              onClick={onWishlistClick}
              aria-label="Хадгалах"
              className="absolute top-3 right-3 z-20 w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/85 hover:bg-white text-foreground transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
            >
              {wishlistLoading ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Heart className={`size-4 ${isWished ? 'fill-[#B12A4E] text-[#B12A4E]' : ''}`} />
              )}
            </button>
          )}
          {allImages.length > 1 && (
            <>
              <div className="absolute bottom-5 left-3 right-15 z-20 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:flex gap-1 items-end">
                {allImages.map((_: string, i: number) => (
                  <div key={i} onMouseEnter={() => setHoveredImageIndex(i)} className="flex-1 h-5 cursor-pointer flex items-end pointer-events-auto">
                    <div className={`w-full h-1 rounded-full transition-all ${heroIndex === i ? 'bg-white' : 'bg-white/50'} drop-shadow-sm`} />
                  </div>
                ))}
              </div>
              <div className="absolute bottom-2 left-0 right-0 z-20 sm:hidden pointer-events-none">
                <div className="flex justify-center gap-1.5">
                  {allImages.map((_: string, i: number) => (
                    <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${heroIndex === i ? 'bg-white' : 'bg-white/50'} drop-shadow-sm`} />
                  ))}
                </div>
              </div>
            </>
          )}
          {canPlaceOrder && isProductAvailable && (
            <button
              onClick={onAddClick}
              aria-label="Сагсанд нэмэх"
              disabled={adding}
              className="absolute bottom-3 right-3 z-30 w-10 h-10 inline-flex items-center justify-center rounded-full bg-foreground text-white shadow-lg hover:bg-foreground/90 disabled:opacity-60 transition-colors sm:opacity-0 sm:group-hover:opacity-100 cursor-pointer disabled:cursor-not-allowed"
            >
              {adding ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
            </button>
          )}
        </div>
        <div className="pt-4 flex flex-col">
          <div className="h-4 text-[11px] uppercase tracking-wider font-semibold text-foreground/50 truncate">
            {product.brand?.name || ''}
          </div>
          <div className="mt-1 flex items-start justify-between gap-3">
            <div className="text-[16px] font-medium text-foreground leading-snug flex-1 min-w-0 line-clamp-1">
              {product.name}
            </div>
            {vendor?.canReview && (
              <span className="inline-flex items-center gap-0.5 text-[12px] text-foreground shrink-0 pt-0.5">
                <Star className="size-3 fill-current" />
                <span>{product.averageRating > 0 ? product.averageRating.toFixed(1) : '0'}</span>
              </span>
            )}
          </div>
          <div className="mt-1.5 flex items-baseline gap-2 whitespace-nowrap">
            {isDiscounted ? (
              <>
                <div className="text-[18px] font-semibold font-gilroy text-red-600">
                  {displayEffective === 0 ? 'Үнэгүй' : formatPrice(displayEffective)}
                </div>
                <div className="text-[13px] text-foreground/40 line-through">{formatPrice(displayStrike)}</div>
              </>
            ) : (
              <div className="text-[18px] font-semibold font-gilroy text-foreground">
                {formatPrice(availableVariant?.price)}
              </div>
            )}
          </div>
          <div className={`mt-2 flex items-center gap-1.5 ${colors.length > 0 ? 'h-6' : 'h-5'}`}>
            <ColorSwatches colors={colors} active={activeColor} onSelect={selectColor} className="flex-nowrap" />
            {colors.length === 0 && product.variants && product.variants.length > 1 && (
              <>
                {product.variants.slice(0, 4).map((v: any) => {
                  const swatch = v.images?.[0]
                  return swatch ? (
                    <span key={v.id} className="size-5 rounded-full bg-foreground/10 overflow-hidden ring-1 ring-foreground/10">
                      <img src={formatImageURL(swatch)} alt={v.name} className="w-full h-full object-cover" />
                    </span>
                  ) : (
                    <span key={v.id} className="size-5 rounded-full bg-foreground/10 ring-1 ring-foreground/10 inline-flex items-center justify-center text-[9px] font-semibold text-foreground/60">
                      {v.name?.charAt(0) || '?'}
                    </span>
                  )
                })}
                {product.variants.length > 4 && (
                  <span className="text-[11px] font-semibold text-foreground/55">+{product.variants.length - 4}</span>
                )}
              </>
            )}
          </div>
          {product.freeShipping && (
            <div className="mt-0.5 text-[12px] text-foreground/60">Үнэгүй хүргэлт</div>
          )}
        </div>
      </a>
      <VariantSelectionModal
        isOpen={showVariantModal}
        onClose={() => setShowVariantModal(false)}
        product={cardProduct}
        vendor={vendor}
        onAddToCart={handleVariantAddToCart}
      />
    </>
  )
}

// memo: appending a page of 20 otherwise re-renders every card already in the grid
export default memo(ProductCard)
